import React, { useState, useEffect } from 'react';
import { Search, Plus, SortAsc, SortDesc } from 'lucide-react';
import Header from './Header';
import { getUserInfo } from './apiUtils';
import CreateProjectDialog from './ManageProjectsComponents/CreateProjectDialog';
import ProjectActionsPanel from './ManageProjectsComponents/ProjectActionsPanel';
import Dialog from './ManageProjectsComponents/Dialog';

// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '0px',
    backgroundColor: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    fontSize: '0.875rem',
  },
  th: {
    backgroundColor: '#E5E4E2',
    color: '#0064e5',
    padding: '6px 12px',
    textAlign: 'left',
    borderBottom: '2px solid #ddd',
    fontSize: '0.875rem',
    lineHeight: '1',
    height: '32px',
    cursor: 'pointer',
    userSelect: 'none',
    position: 'relative'
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #ddd',
  },
  checkbox: {
    marginRight: '10px',
  },
  button: {
    padding: '6px 12px',
    margin: '0 5px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  error: {
    color: 'red',
    padding: '10px',
    marginBottom: '10px',
  },
};

const ManageProjects = () => {
  const [accounts, setAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState(new Set());
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedProjectForEdit, setSelectedProjectForEdit] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [permissions, setPermissions] = useState({
    canUpdate: false,
    canViewProjectList: false,
    canViewOtherAccounts: false,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);

  const login_token = localStorage.getItem('authToken');
  const [corporateAccount, setCorporateAccount] = useState('');
  const [currentProject, setCurrentProject] = useState('');
  const [userId, setUserId] = useState('');
  const [accountDescription, setAccountDescription] = useState('');
  const [userName, setUserName] = useState('');
  const [redirectPath, setRedirectPath] = useState('');

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const showErrorDialog = (message, path) => {
    setDialogMessage(message);
    setRedirectPath(path);
    setShowDialog(true);
  };

  const checkPermission = async (categoryHeader, categorySubHeaders) => {
    console.log(`Checking permissions for ${categoryHeader} with sub-headers:`, categorySubHeaders);

    try {
      const response = await fetch(appConfig.getUrl('/get_user_access_level_new'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          user_id: userId,
          project_id: currentProject,
          category_header: categoryHeader,
          category_sub_header: ""
        }),
      });
      const data = await response.json();
      console.log(`API response for ${categoryHeader}:`, data);

      const newPermissions = {};

      data.user_actions_list.forEach(item => {
        console.log(`Processing user action: ${item.user_action}, has_access: ${item.has_access}`);

        switch(item.user_action) {
          case 'Create Or Edit Project':
            newPermissions.canUpdate = item.has_access;
            break;
          case 'View Project List':
            newPermissions.canViewProjectList = item.has_access;
            console.log(`User has permission to view project list: ${item.has_access}`);
            break;
          case 'View Other Accounts List':
            newPermissions.canViewOtherAccounts = item.has_access;
            console.log(`User has permission to view other accounts list: ${item.has_access}`);
            break;
        }
      });

      setPermissions(prevPermissions => ({
        ...prevPermissions,
        ...newPermissions
      }));

      // Return the new permissions so they can be used immediately
      return {
        success: data.access_status && data.status === 'Success',
        permissions: newPermissions
      };
    } catch (error) {
      console.error(`Error checking permission for ${categoryHeader}:`, error);
      return {
        success: false,
        permissions: {}
      };
    }
  };

  const fetchAccounts = async (canViewOtherAccounts) => {
    try {
      const response = await fetch(appConfig.getUrl('/get_account_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          user_id: canViewOtherAccounts ? '' : userId,
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setAccounts(data.account_list);
        console.log('Accounts fetched successfully:', data.account_list.length, 'accounts');
      } else {
        console.error('Failed to fetch accounts:', data.status_description);
        setAccounts([]);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setAccounts([]);
    }
  };

  const fetchProjects = async (accountId) => {
    setProjects([]);

    console.log('Fetching projects for account:', accountId);

    if (!permissions.canViewProjectList) {
      console.log('User does not have permission to view project list');
      return;
    }

    if (!accountId) {
      return;
    }

    try {
      const response = await fetch(appConfig.getUrl('/get_project_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: accountId
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        let filteredProjects = data.project_list;

        if (searchFilter) {
          filteredProjects = filteredProjects.filter(project =>
            project.project_description.toLowerCase().includes(searchFilter.toLowerCase()) ||
            project.project_id.toLowerCase().includes(searchFilter.toLowerCase()) ||
            project.functional_domain.toLowerCase().includes(searchFilter.toLowerCase()) ||
            project.project_prefix.toLowerCase().includes(searchFilter.toLowerCase())
          );
        }

        if (sortConfig.key) {
          filteredProjects.sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (aValue < bValue) {
              return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
              return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
          });
        }

        setProjects(filteredProjects);
      } else {
        console.error('Failed to fetch projects:', data.status_description);
        setProjects([]);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    }
  };

  const handleAccountSelect = (accountId) => {
    setSelectedAccountId(accountId);
    setSelectedProjects(new Set());
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearch = () => {
    setSearchFilter(searchInput);
  };

  const handleSearchInputChange = (e) => {
    const newValue = e.target.value;
    setSearchInput(newValue);

    if (newValue === '') {
      setSearchFilter('');
    }
  };

  const getSelectedAccountDescription = () => {
    if (selectedAccountId) {
      const account = accounts.find(acc => acc.corporate_account === selectedAccountId);
      return account ? account.account_description : '';
    }
    return '';
  };

  const refreshProjects = () => {
    if (permissions.canViewProjectList && selectedAccountId) {
      fetchProjects(selectedAccountId);
    }
    setSelectedProjects(new Set());
  };

  // Initial load useEffect - Get user info
  useEffect(() => {
    const setInitialLoad = async () => {
      if (!login_token) {
        showErrorDialog('Invalid login credentials. Please login first', '/LoginAndAuthentication');
        return;
      }

      try {
        const result = await getUserInfo({
          login_token: login_token
        });

        if (result.success) {
          setUserId(result.user_id);
          setUserName(result.userInfo.user_name);
          setCorporateAccount(result.userInfo.corporate_account);
          setAccountDescription(result.userInfo.account_description);
          setCurrentProject(result.userInfo.default_project || 'default');
        }
      } catch (error) {
        console.error('Error calling API:', error);
      }
    };

    setInitialLoad();
  }, []);

  // Main initialization useEffect - Check permissions and fetch accounts
  useEffect(() => {
    const initializeApp = async () => {
      if (!userId || !corporateAccount) return;

      try {
        console.log('Starting app initialization...');

        // Clear accounts first to prevent race conditions
        setAccounts([]);

        // Step 1: Check admin permissions
        console.log('Checking admin permissions...');
        const adminResult = await checkPermission('Admin Functions', ['Create Or Edit Project', 'View Project List']);

        // Step 2: Check super admin permissions
        console.log('Checking super admin permissions...');
        const superAdminResult = await checkPermission('Super Admin Functions', ['View Other Accounts List']);

        // Step 3: Fetch accounts using the most recent permission result
        console.log('Fetching accounts with permissions:', {
          canViewOtherAccounts: superAdminResult.permissions?.canViewOtherAccounts
        });

        await fetchAccounts(superAdminResult.permissions?.canViewOtherAccounts || false);

        console.log('App initialization complete');

      } catch (error) {
        console.error('Error initializing app:', error);
      }
    };

    initializeApp();
  }, [userId, corporateAccount]);

  // Projects fetching useEffect
  useEffect(() => {
    console.log('useEffect for selectedAccountId, searchFilter, sortConfig, permissions.canViewProjectList triggered --', selectedAccountId);
    console.log('can View Project List Permission:', permissions.canViewProjectList);

    if (selectedAccountId && permissions.canViewProjectList) {
      console.log('Fetching projects for selected account:', selectedAccountId);
      fetchProjects(selectedAccountId);
    } else if (selectedAccountId && !permissions.canViewProjectList) {
      setProjects([]);
    }
  }, [selectedAccountId, searchFilter, sortConfig, permissions.canViewProjectList]);

  // Auto-select account if only one exists
  useEffect(() => {
    if (accounts.length === 1 && !selectedAccountId) {
      const singleAccount = accounts[0];
      console.log('Auto-selecting single account:', singleAccount.corporate_account);
      setSelectedAccountId(singleAccount.corporate_account);
      setSelectedProjects(new Set());
      setCurrentPage(1);
    }
  }, [accounts, selectedAccountId]);

  // Pagination useEffects
  useEffect(() => {
    setCurrentPage(1);
  }, [searchFilter, sortConfig]);

  useEffect(() => {
    const calculatedTotalPages = Math.ceil(projects.length / itemsPerPage);
    if (currentPage > Math.max(1, calculatedTotalPages)) {
      setCurrentPage(Math.max(1, calculatedTotalPages));
    }
  }, [projects, currentPage, itemsPerPage]);

  // Add pagination calculation logic
  const totalPages = Math.ceil(projects.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProjects = projects.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div style={{ marginTop: '80px' }}>
        {/* Header section */}
        <div className="pt-2 pb-2">
          <div className="flex items-center px-6 mb-0 justify-between">
            <div className="w-80">
              <div className="flex items-center relative">
                <input
                  type="text"
                  value={searchInput}
                  onChange={handleSearchInputChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  placeholder="Search projects..."
                  className="p-2 pr-4 border rounded w-full"
                />
                <button
                  onClick={handleSearch}
                  className="ml-2 p-1 rounded bg-blue-500 hover:bg-blue-600 text-white"
                  aria-label="Search"
                >
                  <Search size={16} />
                </button>
              </div>
            </div>

            <h1 className="text-xl font-bold text-center flex-1">Manage Projects</h1>

            <div className="w-80"></div>
          </div>

          <div className="flex items-center px-6 mb-0 bg-blue-100 py-0 rounded-lg">
            {/* Selected Account Path - Left aligned */}
            <div className="flex-1 text-left">
              <span className="text-base font-bold text-blue-700">Selected Account: </span>
              <span className="text-sm">
                {selectedAccountId ? (
                  <>
                    <span className="text-green-600">
                      {getSelectedAccountDescription()} ({selectedAccountId})
                    </span>
                    <button
                      onClick={() => {
                        setSelectedAccountId(null);
                        setProjects([]);
                      }}
                      className="px-3 py-1 text-xs bg-red-300 text-white rounded hover:bg-red-500 items-center ml-4">
                      X Clear Selection
                    </button>
                  </>
                ) : (
                  <span style={{ color: 'red' }}>None Selected</span>
                )}
              </span>
            </div>

            {/* Current User Info - Center aligned */}
            <div className="flex-1 text-center">
              <h2 className="text-base font-bold text-blue-700">Current Project: {currentProject}</h2>
            </div>

            {/* Create Project Button - Right aligned */}
            <div className="flex-1 flex justify-end items-center relative group">
              <button
                disabled={!selectedAccountId || !permissions.canUpdate}
                onClick={() => {
                  if (selectedAccountId && permissions.canUpdate) {
                    setSelectedProjectForEdit(null);
                    setIsEditMode(false);
                    setIsCreateDialogOpen(true);
                  } else if (!selectedAccountId) {
                    setDialogMessage('Please select an account from the left panel first');
                    setShowDialog(true);
                  } else if (!permissions.canUpdate) {
                    setDialogMessage('You do not have permission to create or edit projects');
                    setShowDialog(true);
                  }
                }}
                className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ${(!selectedAccountId || !permissions.canUpdate) ? 'opacity-50' : ''}`}
              >
                <Plus size={18} className="mr-2" />
                <span>Create New Project</span>
              </button>

              {(!permissions.canUpdate || !selectedAccountId) && (
                <div className="absolute right-0 top-full mt-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                  {!selectedAccountId
                    ? "Select an account from the left panel to enable this button"
                    : !permissions.canUpdate
                    ? "You do not have permission to create or edit projects"
                    : ""}
                  <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-600"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-64px)]">
        {/* Left Panel - Accounts - FIXED VERSION */}
        <div className="mt-0 w-64 bg-blue-800 border-r overflow-y-auto">
          <div className="p-4">
            <div className="flex justify-center items-center mb-4 bg-white p-2 rounded-lg shadow">
              <h2 className="font-semibold text-sm text-blue-600">Select Account</h2>
            </div>

            {accounts.map(account => (
              <div
                key={account.corporate_account}
                className={`flex items-center py-2 px-2 cursor-pointer rounded transition-all duration-200 ${
                  selectedAccountId === account.corporate_account
                    ? 'bg-blue-100 text-blue-800'
                    : 'text-white hover:bg-white hover:text-blue-600'
                }`}
                onClick={() => handleAccountSelect(account.corporate_account)}
              >
                <span className="ml-2 text-sm">
                  {account.account_description} ({account.corporate_account})
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex gap-4">
          {/* Main table area */}
          <div className="flex-1 overflow-y-auto p-0 relative">
            {!permissions.canViewProjectList ? (
              <div className="flex items-center justify-center h-full">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 max-w-md mx-auto">
                  <div className="flex items-center justify-center mb-4">
                    <svg className="w-12 h-12 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-yellow-800 mb-3 text-center">Access Restricted</h3>
                  <p className="text-yellow-700 text-center">
                    You do not have permission to view the project list. Please contact your administrator to request access.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex-1">
                <div className="bg-white rounded-lg shadow">
                  <table style={styles.table}>
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th style={styles.th}>
                          <input
                            type="checkbox"
                                disabled
                            checked={projects.length > 0 && selectedProjects.size === projects.length}
                            onChange={(e) => {
                              const newSelected = new Set();
                              if (e.target.checked) {
                                projects.forEach(project => newSelected.add(project.project_id));
                              }
                              setSelectedProjects(newSelected);
                            }}
                          />
                        </th>
                        <th
                          style={styles.th}
                          onClick={() => handleSort('project_id')}
                          className="select-none hover:bg-gray-200 transition-colors"
                        >
                          <div className="flex items-center">
                            <span style={{ textDecoration: 'underline' }}>Project Name</span>
                            <span className="ml-1">
                              {sortConfig.key === 'project_id' ? (
                                sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                              ) : null}
                            </span>
                          </div>
                        </th>
                        <th
                          style={styles.th}
                          onClick={() => handleSort('project_description')}
                          className="select-none hover:bg-gray-200 transition-colors"
                        >
                          <div className="flex items-center">
                            <span style={{ textDecoration: 'underline' }}>Project Description</span>
                            <span className="ml-1">
                              {sortConfig.key === 'project_description' ? (
                                sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                              ) : null}
                            </span>
                          </div>
                        </th>
                        <th
                          style={styles.th}
                          onClick={() => handleSort('functional_domain')}
                          className="select-none hover:bg-gray-200 transition-colors"
                        >
                          <div className="flex items-center">
                            <span style={{ textDecoration: 'underline' }}>Functional Domain</span>
                            <span className="ml-1">
                              {sortConfig.key === 'functional_domain' ? (
                                sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                              ) : null}
                            </span>
                          </div>
                        </th>
                        <th
                          style={styles.th}
                          onClick={() => handleSort('project_prefix')}
                          className="select-none hover:bg-gray-200 transition-colors"
                        >
                          <div className="flex items-center">
                            <span style={{ textDecoration: 'underline' }}>Project Prefix</span>
                            <span className="ml-1">
                              {sortConfig.key === 'project_prefix' ? (
                                sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                              ) : null}
                            </span>
                          </div>
                        </th>

                        <th
                          style={styles.th}
                          onClick={() => handleSort('created_date')}
                          className="select-none hover:bg-gray-200 transition-colors"
                        >
                          <div className="flex items-center">
                            <span style={{ textDecoration: 'underline' }}>Created Date</span>
                            <span className="ml-1">
                              {sortConfig.key === 'created_date' ? (
                                sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                              ) : null}
                            </span>
                          </div>
                        </th>
                        <th
                          style={styles.th}
                          onClick={() => handleSort('updated_date')}
                          className="select-none hover:bg-gray-200 transition-colors"
                        >
                          <div className="flex items-center">
                            <span style={{ textDecoration: 'underline' }}>Updated Date</span>
                            <span className="ml-1">
                              {sortConfig.key === 'updated_date' ? (
                                sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                              ) : null}
                            </span>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedProjects.map(project => (
                        <tr
                          key={project.project_id}
                          className="border-t hover:bg-gray-50"
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              disabled
                              checked={selectedProjects.has(project.project_id)}
                              onChange={() => {
                                const newSelected = new Set(selectedProjects);
                                if (newSelected.has(project.project_id)) {
                                  newSelected.delete(project.project_id);
                                } else {
                                  newSelected.add(project.project_id);
                                }
                                setSelectedProjects(newSelected);
                              }}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="relative group">
                              <button
                                className={`text-blue-600 hover:text-blue-800 hover:underline focus:outline-none ${
                                  !permissions.canUpdate ? 'opacity-50 ' : ''
                                }`}
                                onClick={() => {
                                  if (permissions.canUpdate) {
                                    setSelectedProjects(new Set([project.project_id]));
                                    setSelectedProjectForEdit(project);
                                    setIsEditMode(true);
                                    setIsCreateDialogOpen(true);
                                  } else {
                                    setDialogMessage('You do not have permission to edit projects');
                                    setShowDialog(true);
                                  }
                                }}
                                disabled={!permissions.canUpdate}
                              >
                                {project.project_id}
                              </button>

                              {!permissions.canUpdate && (
                                <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                                  You do not have permission to edit projects
                                  <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-600"></div>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">{project.project_description}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800">
                              {project.functional_domain || 'Not set'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                              {project.project_prefix || 'Not set'}
                            </span>
                          </td>

                          <td className="px-4 py-3">
                            <span className="text-xs text-gray-600">
                              {formatDate(project.created_date)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-gray-600">
                              {formatDate(project.updated_date)}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {projects.length === 0 && (
                        <tr>
                          <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                            No projects found. {selectedAccountId ? 'Create a new project or adjust your search.' : 'Select an account to view projects.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination and project count information */}
                {projects.length > 0 && (
                  <div className="mt-4 flex justify-between items-center bg-white p-4 rounded-lg shadow">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span>Total Projects: {projects.length}</span>
                      {selectedProjects.size > 0 && (
                        <span>| Selected: {selectedProjects.size}</span>
                      )}
                      <span>| Showing {projects.length > 0 ? startIndex + 1 : 0} - {Math.min(startIndex + itemsPerPage, projects.length)} of {projects.length}</span>
                    </div>

                    {/* Pagination controls */}
                    <div className="flex items-center gap-2">
                      <button
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        aria-label="Previous page"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <span className="text-sm text-blue-600">Page {currentPage} of {Math.max(1, totalPages)}</span>
                      <button
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages || totalPages === 0}
                        aria-label="Next page"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>

                    {/* Items per page selector */}
                    <div className="flex items-center">
                      <span className="text-sm text-gray-600 mr-2">Items per page:</span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="border rounded p-1 text-sm"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={15}>15</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right sidebar with project actions */}

        </div>
      </div>

      {/* Dialog components */}
      {showDialog && (
        <Dialog
          message={dialogMessage}
          onClose={() => {
            setShowDialog(false);
          }}
          onCancel={() => setShowDialog(false)}
        />
      )}

      <CreateProjectDialog
        isOpen={isCreateDialogOpen}
        onClose={() => {
          setIsCreateDialogOpen(false);
          setIsEditMode(false);
          setSelectedProjectForEdit(null);
        }}
        onSubmit={refreshProjects}
        selectedAccountId={selectedAccountId}
        isEditMode={isEditMode}
        projectToEdit={selectedProjectForEdit}
        permissions={permissions}
        loginToken={login_token}
        corporateAccount={corporateAccount}
        currentProject={currentProject}
      />
    </div>
  );
};

export default ManageProjects;